import type { RichTextField, RTNode } from '@prismicio/client';
import { tick } from 'svelte';

export function isRichTextField(value: unknown): value is RichTextField {
	return Array.isArray(value) && value.every((node) => isRTNode(node));
}

function isRTNode(node: unknown): node is RTNode {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return typeof node === 'object' && node !== null && 'type' in (node as any);
}

export class AnimatedTitle {
	titleElement: HTMLElement;
	titleText: string;
	percentageToAnimate: number;
	animation: ReturnType<typeof setInterval> | undefined = undefined;
	animationSpeed: number = 20;
	fadeSpeed: number = 50;
	wgtRange: number[] = Array.from({ length: (900 - 100) / 10 + 1 }, (_, i) => 100 + i * 10);
	variationCssProperty: string = '--font-variation-setting';

	constructor(title: HTMLElement, percentageToAnimate: number) {
		this.titleElement = title;
		this.titleText = this.titleElement.innerText;
		this.percentageToAnimate = percentageToAnimate;

		if (!this.titleElement || !this.percentageToAnimate) return;

		this.createAnimationStructure();
		this.initAnimation();
	}

	createAnimationStructure() {
		this.splitToWords();
	}

	splitToWords() {
		const wordArray = this.titleElement.innerText.split(' ');
		const newHtml: string[] = [];

		[...wordArray].forEach((word) => {
			const chars = this.splitWordIntoCharacters(word);
			const html = `<span data-type="word">${chars.join('')}</span>`;
			newHtml.push(html);
		});

		this.titleElement.innerHTML = newHtml.join(' ');
	}

	splitWordIntoCharacters(word: string) {
		const stringArray = word.split('');
		const newHtml: Array<string> = [];

		[...stringArray].forEach((char, index) => {
			const html = `<span data-type="char" class="char" style="--font-animation-speed:${this.fadeSpeed}ms;--char-index:${index}">${char}</span>`;
			newHtml.push(html);
		});

		return newHtml;
	}

	async initAnimation() {
		await tick();

		const words = this.titleElement.querySelectorAll('span[data-type=word]');

		[...words].forEach((word) => {
			const characters = word.querySelectorAll('span[data-type=char]');
			const length = characters.length;
			const charsToSelect = Math.floor((length / 100) * this.percentageToAnimate);
			const nodes = Array.from(characters);

			// shuffle array
			for (let i = nodes.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				[nodes[i], nodes[j]] = [nodes[j], nodes[i]];
			}

			const selectedChars = nodes.slice(0, charsToSelect);
			[...selectedChars].forEach((char) => {
				const element = char as HTMLElement;
				element.classList.add('animate-out');
				element.classList.add('animate');
				element.style.setProperty(
					`${this.variationCssProperty}`,
					`"wght" ${this.wgtRange[0]}`
				);
			});
		});

		setTimeout(() => this.startAnimation(), this.fadeSpeed);
	}

	async startAnimation() {
		await tick();
		const chars = this.titleElement.querySelectorAll('span.animate');
		let wghtIndex: number = 0;
		let reverse: boolean = false;

		[...chars].forEach((char) => {
			const element = char as HTMLElement;
			element.classList.remove('animate-out');
		})

		this.animation = setInterval(() => {
			[...chars].forEach((char) => {
				const element = char as HTMLElement;
				element.style.setProperty(
					`${this.variationCssProperty}`,
					`"wght" ${this.wgtRange[wghtIndex]}`
				);
			});

			if (!reverse) {
				if (wghtIndex < this.wgtRange.length - 1) {
					++wghtIndex;
				} else {
					reverse = true;
				}
			} else {
				if (wghtIndex >= 0) {
					--wghtIndex;
				} else {
					reverse = false;
					// this.endAnimation();
				}
			}
		}, this.animationSpeed);
	}

	endAnimation() {
		const chars = this.titleElement.querySelectorAll('span.animate');

		clearInterval(this.animation);

		[...chars].forEach((char) => {
			const element = char as HTMLElement;
			element.classList.add('animate-out');
			element.style.removeProperty(this.variationCssProperty);
		});

		setTimeout(() => {
			[...chars].forEach((char) => {
				const element = char as HTMLElement;
				element.classList.remove('animate');
				element.classList.remove('animate-out');
			});

			this.initAnimation();
		}, this.fadeSpeed);
	}
}
